import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Alert,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import {
  ArrowDownward as ArrowDownwardIcon,
  ArrowUpward as ArrowUpwardIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import CreateInvoiceModal from "../components/CreateInvoiceModal";
import InvoiceFilters from "../components/InvoiceFilters";
import InvoiceList from "../components/InvoiceList";
import StatsSummary from "../components/StatsSummary";
import {
  Invoice,
  InvoiceFilters as IInvoiceFilters,
  InvoiceSummary,
} from "../types/invoice";
import { ApiService } from "../services/api";

const InvoicesPage: React.FC = () => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [stats, setStats] = useState<InvoiceSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<IInvoiceFilters>({});
  const [sortOrder, setSortOrder] = useState<"ASC" | "DESC">("DESC");
  const [sortBy, setSortBy] = useState<"data_emissao" | "id" | "valor">("data_emissao");
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  });

  // Ouve evento de navegação vindo do CompanyManager
  React.useEffect(() => {
    const handler = (e: any) => {
      if (e.detail?.nif) {
        console.log(
          "InvoicesPage: Recebido evento navigateToInvoices com NIF:",
          e.detail.nif
        );
        setFilters((prev) => ({ ...prev, nif_emitente: e.detail.nif }));
      }
    };
    window.addEventListener("navigateToInvoices", handler);
    return () => window.removeEventListener("navigateToInvoices", handler);
  }, []);

  // Load invoices
  const loadInvoices = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await ApiService.getInvoices({
        ...filters,
        page: pagination.page,
        limit: pagination.limit,
        sort_order: sortOrder,
        sort_by: sortBy,
      });

      setInvoices(response.data || []);
      if (response.pagination) {
        setPagination((prev) => ({
          ...prev,
          total: response.pagination!.total,
          pages: response.pagination!.pages,
        }));
      }
    } catch (err) {
      setError("Erro ao carregar faturas. Tente novamente.");
      console.error("Load invoices error:", err);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.limit, sortOrder, sortBy]);

  // Load statistics
  const loadStats = async (statsFilters: Partial<IInvoiceFilters> = {}) => {
    setStatsLoading(true);

    try {
      const response = await ApiService.getInvoiceStats(statsFilters);
      if (response.success) {
        setStats(response.data || []);
      }
    } catch (err) {
      console.error("Error loading stats:", err);
    } finally {
      setStatsLoading(false);
    }
  };

  // Handle filter changes
  const handleFiltersChange = (newFilters: IInvoiceFilters) => {
    setFilters(newFilters);
    setPagination((prev) => ({ ...prev, page: 1 }));
    loadStats(newFilters);
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  // Handle invoice deletion
  const handleInvoiceDeleted = () => {
    loadInvoices();
    loadStats(filters);
  };

  // Initial load
  useEffect(() => {
    loadStats();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load invoices when filters or pagination change
  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Page Header */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="h4" gutterBottom>
            Lista de Faturas
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Consulte e gerencie todas as faturas digitalizadas via QR code.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateModalOpen(true)}
        >
          Nova Fatura
        </Button>
      </Box>

      {/* Statistics Summary */}
      <Box>
        <StatsSummary stats={stats} loading={statsLoading} />
      </Box>

      {/* Filters and List */}
      <Box
        sx={{
          display: "flex",
          gap: 3,
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        {/* Filters */}
        <Box sx={{ width: { xs: "100%", md: "33%" } }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Filtros
            </Typography>
            <InvoiceFilters
              filters={filters}
              onFiltersChange={handleFiltersChange}
              loading={loading}
            />
          </Paper>
        </Box>

        {/* Invoice List */}
        <Box sx={{ width: { xs: "100%", md: "67%" } }}>
          <Paper sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="h6">
                Faturas ({pagination.total})
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <ToggleButtonGroup
                  size="small"
                  value={sortBy}
                  exclusive
                  onChange={(_, value) => {
                    if (value) {
                      setSortBy(value);
                      setPagination((prev) => ({ ...prev, page: 1 }));
                    }
                  }}
                >
                  <ToggleButton value="data_emissao">Data</ToggleButton>
                  <ToggleButton value="id">ID</ToggleButton>
                  <ToggleButton value="valor">Valor</ToggleButton>
                </ToggleButtonGroup>
                <Tooltip title={sortOrder === "DESC" ? "Descendente" : "Ascendente"}>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSortOrder((prev) => (prev === "DESC" ? "ASC" : "DESC"));
                      setPagination((prev) => ({ ...prev, page: 1 }));
                    }}
                    color="primary"
                  >
                    {sortOrder === "DESC" ? <ArrowDownwardIcon /> : <ArrowUpwardIcon />}
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {loading ? (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
              </Box>
            ) : (
              <InvoiceList
                invoices={invoices}
                pagination={pagination}
                filters={filters}
                sortOrder={sortOrder}
                onPageChange={handlePageChange}
                onInvoiceDeleted={handleInvoiceDeleted}
              />
            )}
          </Paper>
        </Box>
      </Box>

      <CreateInvoiceModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onCreated={() => {
          loadInvoices();
          loadStats(filters);
        }}
      />
    </Box>
  );
};

export default InvoicesPage;
